import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Button } from './button';
import { cn } from './utils/shadcn-utils';
const parseAcceptExtensions = (accept) => {
    if (!accept) {
        return undefined;
    }
    return accept
        .split(',')
        .map(s => s.trim().toLowerCase())
        .filter(Boolean);
};
const fileMatchesAccept = (file, acceptList) => {
    if (!acceptList || acceptList.length === 0) {
        return true;
    }
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();
    for (const rule of acceptList) {
        if (rule.startsWith('.')) {
            if (fileName.endsWith(rule)) {
                return true;
            }
        }
        else if (rule.endsWith('/*')) {
            const prefix = rule.slice(0, -1); // keep slash
            if (fileType.startsWith(prefix)) {
                return true;
            }
        }
        else if (fileType === rule) {
            return true;
        }
    }
    return false;
};
const FileUpload = React.forwardRef((props, ref) => {
    const { value, onChange, fileAccept, disabled, className, placeholder, buttonLabel } = props;
    const inputRef = React.useRef(null);
    React.useImperativeHandle(ref, () => inputRef.current);
    const acceptList = React.useMemo(() => parseAcceptExtensions(fileAccept), [fileAccept]);
    const openFileDialog = React.useCallback(() => {
        if (disabled) {
            return;
        }
        if (inputRef.current) {
            inputRef.current.click();
        }
    }, [disabled]);
    const handleInputChange = React.useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) {
            if (onChange) {
                onChange(undefined);
            }
            return;
        }
        if (fileMatchesAccept(file, acceptList)) {
            if (onChange) {
                onChange(file);
            }
        }
        else {
            // Reset input if file does not match
            if (inputRef.current) {
                inputRef.current.value = '';
            }
        }
    }, [onChange, acceptList]);
    const handleDrop = React.useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) {
            return;
        }
        const file = e.dataTransfer.files?.[0];
        if (file && fileMatchesAccept(file, acceptList)) {
            if (onChange) {
                onChange(file);
            }
        }
    }, [onChange, disabled, acceptList]);
    const handleDragOver = React.useCallback((e) => {
        if (disabled) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
    }, [disabled]);
    const clear = React.useCallback(() => {
        if (onChange) {
            onChange(undefined);
        }
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    }, [onChange]);
    return (_jsxs("div", { className: cn('relative w-full rounded-md border border-input-border bg-input', 'data-[disabled=true]:opacity-50 data-[disabled=true]:cursor-not-allowed', className), "data-disabled": disabled ? 'true' : 'false', children: [_jsx("input", { ref: inputRef, type: "file", className: "hidden", accept: fileAccept, disabled: disabled, onChange: handleInputChange }), _jsxs("div", { role: "button", tabIndex: disabled ? -1 : 0, "aria-label": "Drop files here or use the button to select a file", onClick: e => {
                    // prevent double open when inner button is clicked
                    if (e.target.closest('button')) {
                        return;
                    }
                    openFileDialog();
                }, onKeyDown: e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openFileDialog();
                    }
                }, onDrop: handleDrop, onDragOver: handleDragOver, className: cn('flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-input-border p-6 text-center', 'hover:bg-accent hover:text-accent-foreground'), children: [_jsx("div", { className: "text-sm text-muted-foreground", children: value ? _jsx("span", { children: value.name }) : _jsx("span", { children: placeholder ?? 'Přetáhněte sem soubor nebo použijte tlačítko' }) }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Button, { type: "button", variant: "outline", disabled: disabled, onClick: e => {
                                    e.stopPropagation();
                                    openFileDialog();
                                }, children: buttonLabel ?? 'Vybrat soubor' }), value && (_jsx(Button, { type: "button", variant: "ghost", disabled: disabled, onClick: e => {
                                    e.stopPropagation();
                                    clear();
                                }, children: "Odebrat" }))] })] })] }));
});
FileUpload.displayName = 'FileUpload';
export { FileUpload };
//# sourceMappingURL=file-upload.js.map